import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  RadioButtonUnchecked as PendingIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { NLExecutionProgress as NLExecutionProgressType } from '../types';
import NLHealingIndicator from './NLHealingIndicator';

interface NLExecutionProgressProps {
  progress: NLExecutionProgressType;
}

const NLExecutionProgress: React.FC<NLExecutionProgressProps> = ({ progress }) => {
  const completedSteps = progress.passed_steps + progress.failed_steps;
  const progressPct = progress.total_steps > 0
    ? (completedSteps / progress.total_steps) * 100
    : 0;

  const statusColor = progress.status === 'passed'
    ? 'success'
    : progress.status === 'failed'
    ? 'error'
    : 'primary';

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Metric cards */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 70, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="subtitle2" fontWeight={700}>{progress.total_steps}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 70, textAlign: 'center', borderColor: 'success.main' }}>
          <Typography variant="caption" color="success.main">Passed</Typography>
          <Typography variant="subtitle2" fontWeight={700} color="success.main">{progress.passed_steps}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 70, textAlign: 'center', borderColor: 'error.main' }}>
          <Typography variant="caption" color="error.main">Failed</Typography>
          <Typography variant="subtitle2" fontWeight={700} color="error.main">{progress.failed_steps}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 70, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Current</Typography>
          <Typography variant="subtitle2" fontWeight={700}>{progress.current_step}/{progress.total_steps}</Typography>
        </Paper>
        {progress.duration_ms > 0 && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 70, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {progress.duration_ms >= 1000
                ? `${(progress.duration_ms / 1000).toFixed(1)}s`
                : `${progress.duration_ms}ms`}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          color={statusColor}
          sx={{ height: 6, borderRadius: 3 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            {completedSteps} of {progress.total_steps} steps
          </Typography>
          <Chip
            size="small"
            label={progress.status.toUpperCase()}
            color={statusColor}
            sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Step results list */}
      <List dense disablePadding>
        {progress.step_results.map((step) => {
          const healing = progress.healed_selectors?.find((h) => h.step_index === step.step_index);

          return (
            <React.Fragment key={step.step_index}>
              <ListItem
                disablePadding
                sx={{
                  py: 0.5,
                  px: 1,
                  mb: 0.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: step.status === 'running'
                    ? 'primary.main'
                    : step.status === 'passed'
                    ? 'success.light'
                    : step.status === 'failed'
                    ? 'error.light'
                    : 'divider',
                  bgcolor: step.status === 'running' ? 'primary.light' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {/* Status icon */}
                  {step.status === 'running' ? (
                    <CircularProgress size={16} />
                  ) : step.status === 'passed' ? (
                    <PassIcon fontSize="small" color="success" />
                  ) : step.status === 'failed' ? (
                    <FailIcon fontSize="small" color="error" />
                  ) : step.status === 'skipped' ? (
                    <SkipIcon fontSize="small" color="disabled" />
                  ) : (
                    <PendingIcon fontSize="small" color="disabled" />
                  )}

                  {/* Step info */}
                  <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 20 }}>
                    {step.step_index + 1}.
                  </Typography>
                  <Chip
                    label={step.action_id}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />

                  <Box sx={{ flexGrow: 1 }} />

                  {/* Healed badge */}
                  {step.healed && (
                    <Chip
                      size="small"
                      label="healed"
                      color="info"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}

                  {/* Duration */}
                  {step.duration_ms != null && (
                    <Typography variant="caption" color="text.secondary">
                      {step.duration_ms}ms
                    </Typography>
                  )}
                </Box>
              </ListItem>

              {/* Error message */}
              {step.status === 'failed' && step.error && (
                <Box sx={{ pl: 5, pb: 0.5 }}>
                  <Typography variant="caption" color="error.main" sx={{ fontFamily: 'monospace' }}>
                    {step.error}
                  </Typography>
                </Box>
              )}

              {/* Healing indicator */}
              {healing && (
                <Box sx={{ pl: 3 }}>
                  <NLHealingIndicator healing={healing} />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default NLExecutionProgress;
